import { buildQueryString } from '@izimi/utils';

import type FetchService from '../FetchService';
import type {
  ApiRequestBodyType,
  ApiRequestPathParamType,
  ApiRequestQueryParamType,
  ApiResponseType,
} from '../types';
import NotaryCertificationApi from './NotaryCertificationApi';
import type {
  ApiNotaryMailConfigurationResponse,
  ApiPreferredNotary,
  GetNotariesQueryParams,
  PushFileValues,
} from './types';

class NotaryApi extends NotaryCertificationApi {
  constructor(protected fetchService: FetchService) {
    super(fetchService);
  }

  getPreferredNotary() {
    return this.fetchService.get<ApiPreferredNotary>('/preferred-notary');
  }

  getNotaries(params?: GetNotariesQueryParams) {
    const qs = buildQueryString(params);
    return this.fetchService.get<ApiResponseType<'listNotaries'>>(`/notaries${qs}`);
  }

  getNotary({ notaryId }: ApiRequestPathParamType<'getNotaryDetails'>) {
    return this.fetchService.get<ApiResponseType<'getNotaryDetails'>>(`/notaries/${notaryId}`);
  }

  createNotary({ notaryId }: ApiRequestBodyType<'addStudyContact'>) {
    return this.fetchService.post<ApiResponseType<'addStudyContact'>>('/notary/contacts', {
      notaryId,
    });
  }

  pushDocument(
    params: PushFileValues,
    onProgress: XMLHttpRequestEventTarget['onprogress'],
    signal: AbortSignal,
  ) {
    const formData = new FormData();
    formData.append('file', params.file);
    formData.append('fileName', params.fileName);
    if (params.destinationUserIds) {
      formData.append('destinationUserIds', params.destinationUserIds.join(','));
    }
    if (params.pushMessage) {
      formData.append('pushMessage', params.pushMessage);
    }

    return this.fetchService.uploadFile<ApiResponseType<'pushFile'>>(
      '/notary/push-document',
      formData,
      onProgress,
      { signal },
    );
  }

  pushDocuments(params: ApiRequestBodyType<'pushDocuments'>) {
    return this.fetchService.post<ApiResponseType<'pushDocuments'>>(
      '/notary/push-documents',
      params,
    );
  }

  getClaimedVault(nrn: string) {
    return this.fetchService.get<ApiResponseType<'getClaimedVault'>>(
      '/notary/claimed-vault',
      undefined,
      {
        'x-nrn': nrn,
      },
    );
  }

  getClaimedVaults(params: ApiRequestQueryParamType<'getClaimedVaults'>) {
    const qs = buildQueryString(params);
    return this.fetchService.get<ApiResponseType<'getClaimedVaults'>>(
      `/notary/claimed-vaults${qs}`,
    );
  }

  getClaimedVaultHistory({ claimedVaultId }: ApiRequestPathParamType<'getClaimedVaultHistory'>) {
    return this.fetchService.get<ApiResponseType<'getClaimedVaultHistory'>>(
      `/notary/claimed-vault/history/${claimedVaultId}`,
    );
  }

  claimableVault(nrn: string) {
    return this.fetchService.get<ApiResponseType<'getDeceasedVaultInfo'>>(
      `/notary/vault/claim`,
      undefined,
      {
        'x-nrn': nrn,
      },
    );
  }

  claimVault(nrn: string) {
    return this.fetchService.post<ApiResponseType<'claimVault'>>('/notary/vault/claim', undefined, {
      'x-nrn': nrn,
    });
  }

  /**
   * Pass a claimed vault to another study
   */
  passClaimedVault(params: ApiRequestQueryParamType<'passClaimedVault'>) {
    const qs = buildQueryString(params);
    return this.fetchService.post<ApiResponseType<'passClaimedVault'>>(`/notary/vault/pass${qs}`);
  }

  /**
   * Transfer a claimed citizen vault to his heirs
   */
  transferClaimedVault(params: ApiRequestQueryParamType<'transferClaimedVault'>) {
    const qs = buildQueryString(params);
    return this.fetchService.post<ApiResponseType<'transferClaimedVault'>>(
      `/notary/vault/transfer${qs}`,
    );
  }

  getHeirs(params: ApiRequestQueryParamType<'getHeirs'>) {
    const qs = buildQueryString(params);
    return this.fetchService.get<ApiResponseType<'getHeirs'>>(`/notary/vault/heirs${qs}`);
  }

  updateHeir(params: ApiRequestBodyType<'editHeir'>) {
    return this.fetchService.put<ApiResponseType<'editHeir'>>('/notary/vault/heirs', params);
  }

  addHeir(params: ApiRequestBodyType<'addHeir'>) {
    return this.fetchService.post<ApiResponseType<'addHeir'>>('/notary/vault/heirs', params);
  }

  deleteHeir({ heirId }: ApiRequestPathParamType<'deleteHeir'>) {
    return this.fetchService.delete<ApiResponseType<'deleteHeir'>>(`/notary/vault/heirs/${heirId}`);
  }

  generateVaultArchiveUrl({ heirId }: ApiRequestPathParamType<'renewArchiveUrl'>) {
    return this.fetchService.post<ApiResponseType<'renewArchiveUrl'>>(
      `/notary/vault/heirs/${heirId}`,
    );
  }

  getVaultOwner(nrn: string) {
    return this.fetchService.get<ApiResponseType<'getVaultOwnerInfo'>>(
      '/notary/vault-owner',
      undefined,
      {
        'x-nrn': nrn,
      },
    );
  }

  getNotaryContacts(params?: ApiRequestQueryParamType<'getContacts'>) {
    const qs = buildQueryString(params);
    return this.fetchService.get<ApiResponseType<'getContacts'>>(`/notary/contacts${qs}`);
  }

  getNotaryContact({ contactId }: ApiRequestPathParamType<'getContactByUserId'>) {
    return this.fetchService.get<ApiResponseType<'getContactByUserId'>>(
      `/notary/contacts/${contactId}`,
    );
  }

  /**
   * Add a study contact for current user based on notary id
   */
  addNotaryStudyContact(params: ApiRequestBodyType<'addStudyContact'>) {
    return this.fetchService.post<ApiResponseType<'addStudyContact'>>('/notary/contacts', params);
  }

  getNotaryDocuments(params?: ApiRequestQueryParamType<'getDocuments'>) {
    const qs = buildQueryString(params);
    return this.fetchService.get<ApiResponseType<'getDocuments'>>(`/v2/notary/documents${qs}`);
  }

  getNotaryDocument({ documentId }: ApiRequestPathParamType<'getDocument'>) {
    return this.fetchService.get<ApiResponseType<'getDocument'>>(`/notary/document/${documentId}`);
  }

  getNotaryMailConfiguration() {
    return this.fetchService.get<ApiNotaryMailConfigurationResponse>('/notary/mail-configuration');
  }

  setNotaryMailConfiguration(mailConfiguration: ApiNotaryMailConfigurationResponse) {
    return this.fetchService.put<ApiNotaryMailConfigurationResponse>(
      '/notary/mail-configuration',
      mailConfiguration,
    );
  }
}

export default NotaryApi;
